/**
 * AddresseePicker — F.4 (ADR-0073): pole „akceptant na następnym etapie" w oknie „Akceptacja"
 * (`AcceptanceDialog`).
 *
 * Pobiera pulę akceptantów DANEGO węzła (`GET /api/processes/instances/:id/pool?nodeId=`)
 * i renderuje listę wyboru. Pole jest wymagane tylko przy „wymagaj wskazania" (`required`,
 * liczy je okno przez `resolveAddresseeFields`); inaczej opcjonalne — puste = cała pula etapu.
 * Gdy `defaultAddresseeUserId` jest ustawiony i należy do pobranej puli — preselekcja
 * (jednorazowo, dopóki rodzic nie ma jeszcze wyboru dla tego węzła). Brakujący albo
 * nieprawidłowy default zostawia pole PUSTE (spójnie z silnikiem, który traktuje nieprawidłowy
 * default jak jego brak): w polu opcjonalnym znaczy to „cała pula", w wymaganym użytkownik musi
 * wybrać osobę sam.
 *
 * Gdy puli nie udało się wczytać, zamiast pustej listy pokazujemy komunikat z „Spróbuj
 * ponownie" — bez niego w polu wymaganym „Wykonaj" czekałoby na wybór, którego nie da się
 * zrobić. Komunikat jest powiązany z listą wyboru (`aria-describedby`), więc czytnik ekranu
 * odczytuje powód, dla którego pole jest wyłączone. Gdy rekord sprawy usunięto (serwer: 404
 * `TARGET_RECORD_DELETED`), komunikat brzmi „Nie znaleziono kartoteki." i nie ma „Spróbuj
 * ponownie" — sprawa „nie istnieje" do przywrócenia kartoteki (decyzję i tak odrzuci serwer).
 * Tak samo przy module kartoteki sprawy wyłączonym po otwarciu okna (serwer: 402
 * `TARGET_MODULE_INACTIVE`) — komunikat „Moduł tej kartoteki jest nieaktywny." bez „Spróbuj
 * ponownie".
 */

import { useEffect, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { Button } from '@/components/Button';
import { Dropdown } from '@/components/Dropdown';
import { InlineMessage } from '@/components/InlineMessage';
import {
  isTargetModuleInactiveError,
  isTargetRecordDeletedError,
  TARGET_MODULE_INACTIVE_I18N_KEY,
  TARGET_RECORD_DELETED_I18N_KEY,
} from '@/lib/processes/approvals-api';
import { fetchDecisionPool } from '@/lib/processes/instance-api';

export interface AddresseePickerProps {
  instanceId: string;
  nodeId: string;
  nodeLabel: string;
  defaultAddresseeUserId?: string;
  value: string;
  onChange: (userId: string) => void;
  /** Pole wymagane (etap z „wymagaj wskazania"): czerwona gwiazdka + `aria-required`. */
  required?: boolean;
  disabled?: boolean;
  testIdPrefix?: string;
}

export function AddresseePicker({
  instanceId,
  nodeId,
  nodeLabel,
  defaultAddresseeUserId,
  value,
  onChange,
  required = false,
  disabled,
  testIdPrefix = 'addressee-picker',
}: AddresseePickerProps) {
  const { t } = useTranslation();
  // Id komunikatu o błędzie wczytania puli — wskazuje je `aria-describedby` listy wyboru.
  const loadErrorId = useId();

  const { data, error, isLoading, isError, isFetching, refetch } = useQuery({
    queryKey: ['processes', 'instance-pool', instanceId, nodeId],
    queryFn: () => fetchDecisionPool(instanceId, nodeId),
    staleTime: 30_000,
  });

  const approvers = data ?? [];
  // Błąd liczy się, dopiero gdy nie ma żadnej puli: nieudane odświeżenie w tle zostawia
  // poprzednio wczytaną, nadal użyteczną listę.
  const loadFailed = isError && data === undefined;
  // Rekord sprawy usunięty (serwer: 404 `TARGET_RECORD_DELETED`) — sprawa „nie istnieje", ponowna
  // próba nic nie zmieni: „Nie znaleziono kartoteki." bez „Spróbuj ponownie".
  const recordDeleted = loadFailed && isTargetRecordDeletedError(error);
  // Moduł kartoteki sprawy nieaktywny (serwer: 402 `TARGET_MODULE_INACTIVE`) — j.w.
  const moduleInactive = loadFailed && isTargetModuleInactiveError(error);

  useEffect(() => {
    if (
      !value &&
      defaultAddresseeUserId &&
      approvers.some((a) => a.id === defaultAddresseeUserId)
    ) {
      onChange(defaultAddresseeUserId);
    }
    // Uruchamiamy tylko gdy pula się zmienia (dociąga async) — `value`/`onChange` celowo
    // pominięte, żeby nie nadpisywać ręcznego wyboru usera przy każdym renderze rodzica.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approvers, defaultAddresseeUserId]);

  const label = t('tenant.processes.actions.addressee.label', { node: nodeLabel });

  return (
    <div className="mt-3" data-testid={testIdPrefix ? `${testIdPrefix}-${nodeId}` : undefined}>
      <label className="block">
        <span className="mb-1 block text-sm text-text-secondary">
          {label}
          {required ? (
            <span
              className="ml-0.5 text-red-600"
              aria-hidden="true"
              data-testid={`${testIdPrefix}-${nodeId}-required`}
            >
              *
            </span>
          ) : null}
        </span>
        <Dropdown
          data-testid={`${testIdPrefix}-${nodeId}-select`}
          aria-label={label}
          aria-required={required || undefined}
          aria-describedby={loadFailed ? loadErrorId : undefined}
          value={value}
          onChange={(userId) => onChange(userId)}
          disabled={disabled || isLoading || loadFailed}
          options={[
            {
              value: '',
              label: isLoading
                ? t('tenant.processes.actions.addressee.loading')
                : t('tenant.processes.actions.addressee.selectPlaceholder'),
            },
            ...approvers.map((a) => ({ value: a.id, label: a.displayName })),
          ]}
        />
      </label>
      {loadFailed ? (
        <div
          className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1"
          data-testid={`${testIdPrefix}-${nodeId}-load-error`}
        >
          <InlineMessage tone="error" id={loadErrorId}>
            {recordDeleted
              ? t(TARGET_RECORD_DELETED_I18N_KEY)
              : moduleInactive
                ? t(TARGET_MODULE_INACTIVE_I18N_KEY)
                : t('tenant.processes.actions.addressee.loadError')}
          </InlineMessage>
          {recordDeleted || moduleInactive ? null : (
            <Button
              variant="ghost"
              size="sm"
              loading={isFetching}
              disabled={disabled}
              data-testid={`${testIdPrefix}-${nodeId}-retry`}
              onClick={() => void refetch()}
            >
              {t('common.retry')}
            </Button>
          )}
        </div>
      ) : null}
    </div>
  );
}
